import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { TokenService } from './token.service';
import { LoginServicesService } from './login-services.service';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { jwtDecode } from 'jwt-decode';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private tokenService: TokenService,
    private loginService: LoginServicesService,
    private router: Router
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Llamadas al API móvil: si 401, sesión QR expirada → re-login
    if (req.url.includes(environment.xigaMovil)) {
      return next.handle(req).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            localStorage.removeItem('tokenMovil');
            this.tokenService.removeToken();
            localStorage.removeItem('data');
            this.router.navigate(['/login']);
            return throwError(() => new HttpErrorResponse({
              error: { error: 'Su sesión de QR ha expirado, inicie sesión nuevamente' },
              status: 401
            }));
          }
          return throwError(() => error);
        })
      );
    }

    if (!req.url.includes(environment.apiUrl)) {
      return next.handle(req);
    }

    // No interceptar el propio refresh para evitar loop
    if (req.url.includes('login/refresh')) {
      return next.handle(this.addToken(req));
    }

    const token = this.tokenService.getToken();

    // Si el token está por expirar (menos de 5 min), renovar proactivamente
    if (token && this.isTokenExpiringSoon(token)) {
      return this.refreshAndRetry(req, next);
    }

    return next.handle(this.addToken(req)).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.refreshAndRetry(req, next);
        }
        return throwError(() => error);
      })
    );
  }

  private addToken(req: HttpRequest<any>): HttpRequest<any> {
    const token = this.tokenService.getToken();
    if (!token) return req;
    return req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });
  }

  private isTokenExpiringSoon(token: string): boolean {
    try {
      const decoded = jwtDecode(token);
      const now = Math.floor(Date.now() / 1000);
      return decoded.exp! - now < 300; // menos de 5 minutos
    } catch {
      return true;
    }
  }

  private refreshAndRetry(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      // Otro request ya está refrescando, esperar a que termine
      return this.refreshSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(() => next.handle(this.addToken(req)))
      );
    }

    this.isRefreshing = true;
    this.refreshSubject.next(null);

    return this.loginService.refreshToken().pipe(
      switchMap((resp: any) => {
        this.isRefreshing = false;
        this.tokenService.setToken(resp.token);
        localStorage.setItem('data', JSON.stringify(jwtDecode(resp.token)));
        this.refreshSubject.next(resp.token);
        return next.handle(this.addToken(req));
      }),
      catchError((err) => {
        this.isRefreshing = false;
        this.tokenService.removeToken();
        localStorage.removeItem('data');
        this.router.navigate(['/login']);
        return throwError(() => err);
      })
    );
  }
}
